import React, { useState, useEffect, useMemo } from "react";
import { Table, Button, Modal, Form, Input, InputNumber, Switch, Tag, Space, Popconfirm, message } from "antd";
import { PlusOutlined, EditOutlined } from "@ant-design/icons";
import axios from "axios";
import { API_DOMAIN } from "../../constants";
import { useAuth } from "../../contexts/AuthContext";

export function LevelsPanel() {
  const { authState } = useAuth();
  const userId = authState?.id;
  const head = useMemo(() => ({ headers: { accessToken: localStorage.getItem("accessToken") } }), []);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_DOMAIN}/approval-levels`, head);
      setLevels(res.data?.data ?? res.data ?? []);
    } catch { message.error("Failed to load levels"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []); // eslint-disable-line

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    form.setFieldsValue({ name: row.name, priority: row.priority, is_active: !!row.is_active });
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      const body = { ...values, is_active: values.is_active ? 1 : 0, userId };
      if (editing) {
        await axios.patch(`${API_DOMAIN}/approval-levels/${editing.id}`, body, head);
        message.success("Level updated");
      } else {
        await axios.post(`${API_DOMAIN}/approval-levels`, body, head);
        message.success("Level created");
      }
      setModalOpen(false);
      fetch();
    } catch (e) {
      message.error(e?.response?.data?.message || "Save failed");
    } finally { setSaving(false); }
  };

  const toggleActive = async (row) => {
    try {
      await axios.patch(`${API_DOMAIN}/approval-levels/${row.id}`, { is_active: row.is_active ? 0 : 1, userId }, head);
      setLevels(prev => prev.map(l => l.id === row.id ? { ...l, is_active: l.is_active ? 0 : 1 } : l));
    } catch { message.error("Failed to update"); }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name", render: v => <strong>{v}</strong> },
    {
      title: "Priority", dataIndex: "priority", key: "priority",
      render: v => <Tag color="blue">Priority {v}</Tag>,
    },
    {
      title: "Status", dataIndex: "is_active", key: "is_active",
      render: v => <Tag color={v ? "green" : "default"}>{v ? "Active" : "Inactive"}</Tag>,
    },
    {
      title: "Actions", key: "actions",
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row)}>Edit</Button>
          <Popconfirm
            title={row.is_active ? "Deactivate this level?" : "Activate this level?"}
            onConfirm={() => toggleActive(row)}
            okText="Yes" cancelText="No"
          >
            <Button size="small" danger={!!row.is_active}>{row.is_active ? "Deactivate" : "Activate"}</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>New Level</Button>
      </div>
      <Table
        dataSource={levels}
        columns={columns}
        rowKey="id"
        loading={loading}
        size="small"
        pagination={false}
      />
      <Modal
        open={modalOpen}
        title={editing ? "Edit Approval Level" : "New Approval Level"}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Required" }]}>
            <Input placeholder="e.g. Vice Manager" />
          </Form.Item>
          <Form.Item name="priority" label="Priority" rules={[{ required: true, message: "Required" }]}
            extra="Higher number = more approval power. Must be unique.">
            <InputNumber min={1} style={{ width: "100%" }} placeholder="e.g. 10" />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
